import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { ThemeSegmented } from '@/components/ThemeToggle';
import { toast } from 'sonner';

interface Doc {
  organisation_name: string; primary_contact_email: string; support_phone: string;
  fsp_number: string; company_reg_number: string; vat_number: string;
  address_street: string; address_city: string; address_province: string; address_postal: string; address_country: string;
  environment: string;
}

export const GeneralSettings: React.FC = () => {
  const [doc, setDoc] = useState<Doc | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get<Doc>('/settings/general').then(r => setDoc(r.data)); }, []);
  if (!doc) return <div>Loading…</div>;
  const save = async (): Promise<void> => {
    setBusy(true);
    try { await api.put('/settings/general', doc); toast.success('Saved'); }
    catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };
  // Render as inline JSX (NOT a nested component) so typing doesn't remount
  // the input and steal focus after each keystroke.
  const field = (k: keyof Doc, label: string) => (
    <div className="space-y-1.5" key={k}><Label>{label}</Label><Input value={String(doc[k] ?? '')} onChange={(e) => setDoc({ ...doc, [k]: e.target.value })} /></div>
  );
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Appearance</CardTitle><CardDescription>Light, dark, or match your system. Applies to this device.</CardDescription></CardHeader>
        <CardContent><ThemeSegmented /></CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>General</CardTitle><CardDescription>Organisation identity + compliance IDs. Environment: {doc.environment}.</CardDescription></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {field('organisation_name', 'Organisation name')}
            {field('primary_contact_email', 'Primary contact email')}
            {field('support_phone', 'Support phone')}
            {field('fsp_number', 'FSP number')}
            {field('company_reg_number', 'Company reg no.')}
            {field('vat_number', 'VAT number')}
            <div className="space-y-1.5 md:col-span-2">
              <Label>Street</Label>
              <AddressAutocomplete
                value={doc.address_street}
                onChange={(v: string) => setDoc({ ...doc, address_street: v })}
                onResolve={(p: { formatted_address: string; city?: string; province?: string; postal_code?: string; country?: string }) => setDoc((d) => d ? ({
                  ...d,
                  address_street: p.formatted_address,
                  address_city: p.city || d.address_city,
                  address_province: p.province || d.address_province,
                  address_postal: p.postal_code || d.address_postal,
                  address_country: p.country || d.address_country,
                }) : d)}
                placeholder="Start typing to auto-fill city, province, postal code and country…"
                testid="general-street"
              />
            </div>
            {field('address_city', 'City')}
            {field('address_province', 'Province')}
            {field('address_postal', 'Postal code')}
            {field('address_country', 'Country')}
          </div>
          <div className="mt-4 flex justify-end"><Button variant="gradient" onClick={save} disabled={busy}>Save changes</Button></div>
        </CardContent>
      </Card>
    </div>
  );
};
